import React, { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import PropTypes from "prop-types"
import DataTable from "ui/DataTable"
import { getHeaders } from "./utils/headers"
import { useStaffTable } from "./hooks/useStaffTable"
import { transformStaffToRow } from "./components/StaffTableRow"
import StaffModals from "./components/StaffModals"
import MobileStaffTable from "../mobileStaffTable"
import { useDevice } from "hooks/useDevice"

const StaffTable = ({
  staff,
  mobileStaff,
  sortField,
  sortOrder,
  handleTableChange,
  loading,
  onEdit,
  currentPage,
  totalPages,
  onLoadMore,
  totalResults
}) => {
  const { t } = useTranslation()
  const { isMobile } = useDevice()
  const userRole = useSelector((state) => state.user.role)

  const {
    deactivateModalVisible,
    activateModalVisible,
    deleteModalVisible,
    selectedStaff,
    getMenuItems,
    handleView,
    handleDeactivateConfirm,
    handleActivateConfirm,
    handleDeleteConfirm,
    handleModalCancel
  } = useStaffTable(t, onEdit, userRole)

  const columns = useMemo(() => getHeaders({ field: sortField, order: sortOrder }, t), [sortField, sortOrder, t])

  const dataSource = useMemo(() => {
    return staff?.map((staffMember) => transformStaffToRow(staffMember, getMenuItems, t))
  }, [staff, getMenuItems, t])

  return (
    <div>
      {isMobile ? (
        <MobileStaffTable
          staff={mobileStaff}
          loading={loading}
          currentPage={currentPage}
          totalPages={totalPages}
          onLoadMore={onLoadMore}
          totalResults={totalResults}
          getMenuItems={getMenuItems}
        />
      ) : (
        <DataTable
          columns={columns}
          dataSource={dataSource}
          pagination={false}
          rowClickKey="key"
          onChange={handleTableChange}
          onRowClick={handleView}
          rowKey={(record) => record.key}
          loading={loading}
        />
      )}
      <StaffModals
        deactivateModalVisible={deactivateModalVisible}
        activateModalVisible={activateModalVisible}
        deleteModalVisible={deleteModalVisible}
        selectedStaff={selectedStaff}
        onDeactivateConfirm={handleDeactivateConfirm}
        onActivateConfirm={handleActivateConfirm}
        onDeleteConfirm={handleDeleteConfirm}
        onCancel={handleModalCancel}
        t={t}
      />
    </div>
  )
}

StaffTable.propTypes = {
  staff: PropTypes.array.isRequired,
  mobileStaff: PropTypes.array,
  sortField: PropTypes.string,
  sortOrder: PropTypes.string,
  handleTableChange: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  onEdit: PropTypes.func,
  currentPage: PropTypes.number,
  totalPages: PropTypes.number,
  onLoadMore: PropTypes.func,
  totalResults: PropTypes.number
}

StaffTable.defaultProps = {
  staff: []
}

export default StaffTable
